import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Pagination, Stack } from "@mui/material";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AuditLogCard from "../components/auditLogs/AuditLogCard";
import AppCircularProgress from "../components/common/AppCircularProgress";
import PageError from "../components/common/PageError";
import DateRangeFilter from "../components/common/DateRangeFilter";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { getAuditLogs } from "../redux/actions/auditLogActions";

const AuditLogsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const today = new Date().toISOString().split("T")[0];

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);
  const [startDate, setStartDate] = useState<string>(today);
  const [endDate, setEndDate] = useState<string>(today);
  const [page, setPage] = useState<number>(1);
  const limit = 12;

  const {
    items: auditLogs,
    pagination,
    loading,
    error,
  } = useAppSelector((state) => state.auditLog);

  const canRead = hasPermission("audit_log:read_all");

  const fetchLogs = useCallback((): void => {
    if (!canRead) return;
    dispatch(
      getAuditLogs({
        page,
        size: limit,
        search: debouncedSearch,
        startDate,
        endDate,
      }),
    );
  }, [dispatch, page, limit, debouncedSearch, startDate, endDate, canRead]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, startDate, endDate]);

  const renderContent = (): React.JSX.Element => {
    if (loading) return <AppCircularProgress />;
    if (error) return <PageError error={error} onRetry={fetchLogs} />;

    if (!auditLogs || auditLogs.length === 0) {
      return (
        <Typography align="center" color="textSecondary" sx={{ my: 10 }}>
          No audit logs found.
        </Typography>
      );
    }

    return (
      <>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 3,
          }}
        >
          {auditLogs.map((log) => (
            <AuditLogCard key={log.id} auditLog={log} />
          ))}
        </Box>

        {pagination?.totalPages && pagination.totalPages > 1 && (
          <Stack sx={{ alignItems: "center", mt: 4 }}>
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={(_, val) => setPage(val)}
              color="primary"
            />
          </Stack>
        )}
      </>
    );
  };

  return (
    <PageContainer
      title="System Audit Logs"
      subtitle="Track user actions and security events."
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search logs..."
      >
        <DateRangeFilter
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </PageToolbar>
      {renderContent()}
    </PageContainer>
  );
};

export default AuditLogsPage;
